/**
 * Shared GLSL. Everything here is plain strings so it can be reused from any three.js host
 * (web, expo-gl) or ported to another engine.
 */

export const NOISE = /* glsl */ `
float hash12(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
float hash13(vec3 p3) {
  p3 = fract(p3 * 0.1031);
  p3 += dot(p3, p3.zyx + 31.32);
  return fract((p3.x + p3.y) * p3.z);
}
vec2 hash22(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.xx + p3.yz) * p3.zy);
}
vec3 hash33(vec3 p3) {
  p3 = fract(p3 * vec3(0.1031, 0.1030, 0.0973));
  p3 += dot(p3, p3.yxz + 33.33);
  return fract((p3.xxy + p3.yxx) * p3.zyx);
}
float vnoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = hash12(i);
  float b = hash12(i + vec2(1.0, 0.0));
  float c = hash12(i + vec2(0.0, 1.0));
  float d = hash12(i + vec2(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}
float vnoise3(vec3 p) {
  vec3 i = floor(p);
  vec3 f = fract(p);
  vec3 u = f * f * (3.0 - 2.0 * f);
  float n000 = hash13(i);
  float n100 = hash13(i + vec3(1, 0, 0));
  float n010 = hash13(i + vec3(0, 1, 0));
  float n110 = hash13(i + vec3(1, 1, 0));
  float n001 = hash13(i + vec3(0, 0, 1));
  float n101 = hash13(i + vec3(1, 0, 1));
  float n011 = hash13(i + vec3(0, 1, 1));
  float n111 = hash13(i + vec3(1, 1, 1));
  return mix(mix(mix(n000, n100, u.x), mix(n010, n110, u.x), u.y), mix(mix(n001, n101, u.x), mix(n011, n111, u.x), u.y), u.z);
}
float fbm2(vec2 p) {
  float s = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    s += a * vnoise(p);
    p = p * 2.03 + vec2(17.1, -9.3);
    a *= 0.5;
  }
  return s / 0.9375;
}
float fbm3n(vec2 p) {
  float s = 0.0;
  float a = 0.5;
  for (int i = 0; i < 3; i++) {
    s += a * vnoise(p);
    p = p * 2.07 + vec2(5.3, 11.7);
    a *= 0.5;
  }
  return s / 0.875;
}
// Large painterly colour swaths shared by the meadow terrain and the grass blades.
float meadowPatch(vec2 p) {
  return fbm3n(p * 0.011) * 0.65 + vnoise(p * 0.043 + 7.0) * 0.35;
}
// Wildflowers, three scales: broad meadow patches (~80 m) where flowers grow at all, tight
// clusters (~10 m) inside them, and (in the flower shader) a sparse scatter of strays.
// Region and clump take a warped, rotated coordinate (flowerWarp) so thresholded value noise
// never shows its grid: drifts get scalloped, wind-blown edges instead of boxy blobs.
vec2 flowerWarp(vec2 p) {
  vec2 q = mat2(0.8, -0.6, 0.6, 0.8) * p;
  return q + (vec2(vnoise(q * 0.045 + 21.0), vnoise(q * 0.045 - 33.0)) - 0.5) * 16.0;
}
float flowerRegion(vec2 q) {
  return smoothstep(0.45, 0.75, vnoise(q * 0.012 + 40.0));
}
float flowerClump(vec2 q) {
  return smoothstep(0.58, 0.82, vnoise(q * 0.11 - 7.0) * 0.7 + vnoise(mat2(0.8, 0.6, -0.6, 0.8) * q * 0.27 + 3.0) * 0.3);
}
float flowerDrift(vec2 p) {
  vec2 q = flowerWarp(p);
  return flowerRegion(q) * (0.25 + 0.75 * flowerClump(q));
}
float flowerSpecies(vec2 p) {
  return floor(clamp(vnoise(p * 0.02 + 13.0) * 1.25 - 0.12, 0.0, 0.999) * 5.0);
}
// Interleaved gradient noise — great for per-pixel dithering of ray marches and blur taps.
float ign(vec2 p) {
  return fract(52.9829189 * fract(dot(p, vec2(0.06711056, 0.00583715))));
}
`;

/** Uniform declarations shared by all world materials. Values are shared JS objects (see SharedUniforms). */
export const WORLD_UNIFORMS = /* glsl */ `
uniform float uTime;
uniform vec3 uSunDir;
uniform vec3 uSunColor;
uniform vec3 uSunGlow;
uniform vec3 uSkyTop;
uniform vec3 uSkyHorizon;
uniform vec3 uSkyBottom;
uniform vec3 uAmbSky;
uniform vec3 uAmbGround;
uniform float uAmbIntensity;
uniform vec3 uShadowTint;
uniform float uWrap;
uniform vec3 uFogColor;
uniform vec4 uFogParams;   // density, heightFalloff, baseHeight, skyBlend
uniform float uSunScatter;
uniform vec4 uWind;        // dir.xy, strength, gust
uniform vec3 uPlayerPos;
uniform vec4 uPlayerVel;   // vel.xyz, speed01
uniform vec3 uSH[9];       // irradiance SH captured from the sky environment ("procedural HDRI")
uniform sampler2D tTrail;  // toroidal trail map around the pearl: R depth, GB travel dir, A written
uniform vec4 uTrailInfo;   // texel size (m), window size (m), enabled, unused
uniform vec4 uTrailWin;    // window centre xz, half size (m), unused
uniform vec4 uAtmos;       // cloud-shadow strength, mist density, mist base, mist thickness
`;

/** Sample the pearl's trail at a world xz position (toroidal: texture wraps every window). */
export const TRAIL = /* glsl */ `
vec4 trailAt(vec2 p) {
  // Only inside the live window — outside it the toroidal texture would repeat old marks.
  vec2 d = abs(p - uTrailWin.xy);
  float inside = 1.0 - smoothstep(uTrailWin.z - 10.0, uTrailWin.z - 2.0, max(d.x, d.y));
  return texture2D(tTrail, p / uTrailInfo.y) * (uTrailInfo.z * inside);
}
`;

export const ATMOSPHERE = /* glsl */ `
vec3 skyGradient(vec3 dir) {
  float h = dir.y;
  float up = pow(clamp(h, 0.0, 1.0), 0.5);
  vec3 col = mix(uSkyHorizon, uSkyTop, up);
  // Mixing warm horizon and blue zenith passes through grey; push saturation back up mid-way.
  float lumC = dot(col, vec3(0.2126, 0.7152, 0.0722));
  col = max(mix(vec3(lumC), col, 1.0 + up * (1.0 - up) * 1.6), 0.0);
  col = mix(col, uSkyBottom, smoothstep(0.0, -0.3, h));
  float sd = max(dot(dir, uSunDir), 0.0);
  col += uSunGlow * (pow(sd, 5.0) * 0.28 + pow(sd, 40.0) * 0.55);
  return col;
}

vec3 fogColorFor(vec3 dir) {
  vec3 horizonDir = normalize(vec3(dir.x, max(dir.y, -0.05) * 0.35, dir.z));
  vec3 c = mix(uFogColor, skyGradient(horizonDir), uFogParams.w);
  float sd = max(dot(dir, uSunDir), 0.0);
  // Forward scattering: a tight glow round the sun and a broad, soft brightening of the whole
  // sun-side of the haze — the air itself catching the light.
  c += uSunGlow * (pow(sd, 7.0) + pow(sd, 2.0) * 0.07) * uSunScatter;
  return c;
}

// Analytic exponential height fog (integrated along the view ray) tinted by the sky in that direction.
float fogAmount(vec3 wpos, out vec3 rd) {
  vec3 ro = cameraPosition;
  vec3 v = wpos - ro;
  float dist = length(v);
  rd = v / max(dist, 1e-4);
  float a = uFogParams.x;
  float b = uFogParams.y;
  float h0 = ro.y - uFogParams.z;
  float k = b * rd.y;
  float integral = abs(k) > 1e-5 ? (1.0 - exp(-dist * k)) / k : dist;
  float amt = a * exp(-b * h0) * integral;
  return 1.0 - exp(-max(amt, 0.0));
}

// Patchy ground mist / blowing haze drifting with the wind, hugging low ground.
float mistAmount(vec3 wpos) {
  if (uAtmos.y <= 0.0) return 0.0;
  float dist = length(wpos - cameraPosition);
  float low = 1.0 - smoothstep(uAtmos.z, uAtmos.z + uAtmos.w, wpos.y);
  vec2 q = wpos.xz * 0.011 - uWind.xy * uTime * 0.06;
  float drift = vnoise(q) * 0.6 + vnoise(q * 2.7 + vec2(3.1, -1.7)) * 0.4;
  return clamp(uAtmos.y * low * smoothstep(0.32, 0.78, drift) * (1.0 - exp(-dist * 0.02)), 0.0, 0.85);
}

vec3 applyFog(vec3 col, vec3 wpos) {
  vec3 rd;
  float f = fogAmount(wpos, rd);
  f = 1.0 - (1.0 - f) * (1.0 - mistAmount(wpos));
  return mix(col, fogColorFor(rd), f);
}

// Same, with the (slowly varying) mist term precomputed per vertex.
vec3 applyFogM(vec3 col, vec3 wpos, float mist) {
  vec3 rd;
  float f = fogAmount(wpos, rd);
  f = 1.0 - (1.0 - f) * (1.0 - mist);
  return mix(col, fogColorFor(rd), f);
}

// Soft contact shadow where the pearl touches the ground (at golden hour its cast shadow falls
// far behind it, so without this it looks like it hovers).
float contactShade(vec3 wp) {
  vec3 d = wp - uPlayerPos;
  float r2 = dot(d.xz, d.xz);
  return 1.0 - 0.6 * exp(-r2 * 2.4) * (1.0 - smoothstep(0.1, 1.0, abs(d.y)));
}

// Pockets of air: discrete gusts travelling downwind across the meadow (0 calm … 1 in a gust).
// Each gust is a soft blob stretched along the wind, with a sharp leading edge and a long tail,
// riding the wind at ~7 m/s; under half of the cells carry one. Coherent, not random.
float gustWave(vec2 p) {
  vec2 wd = normalize(uWind.xy + vec2(1e-4, 0.0));
  vec2 q = vec2(dot(p, wd), dot(p, vec2(-wd.y, wd.x)));
  q.x -= uTime * 7.0;
  vec2 cellSize = vec2(36.0, 24.0);
  vec2 base = floor(q / cellSize - 0.5);
  float g = 0.0;
  for (int j = 0; j <= 1; j++) {
    for (int i = 0; i <= 1; i++) {
      vec2 cc = base + vec2(float(i), float(j));
      vec2 h = hash22(cc * 1.37 + 3.1);
      float on = step(0.55, hash12(cc * 0.73 + 9.2));
      vec2 d = q - (cc + 0.25 + 0.5 * h) * cellSize;
      float size = 0.65 + 0.55 * hash12(cc + 5.0);
      float lx = d.x > 0.0 ? d.x / (6.0 * size) : d.x / (14.0 * size);
      float ly = d.y / (8.5 * size);
      g = max(g, on * exp(-(lx * lx + ly * ly)) * (0.75 + 0.25 * h.x));
    }
  }
  return g * uWind.z;
}

// Big soft cloud shadows sliding across the land with the wind.
float cloudShade(vec3 wp) {
  if (uAtmos.x <= 0.0) return 1.0;
  vec2 q = wp.xz * 0.0021 - uWind.xy * uTime * 0.011;
  float c = vnoise(q) * 0.62 + vnoise(q * 2.3 + vec2(5.2, 1.3)) * 0.38;
  return 1.0 - uAtmos.x * smoothstep(0.5, 0.72, c);
}
`;

export const LIGHTING = /* glsl */ `
// Image-based diffuse ambient from 9 SH coefficients (three.js LightProbe convention).
vec3 shIrradiance(vec3 n) {
  float x = n.x, y = n.y, z = n.z;
  vec3 r = uSH[0] * 0.886227;
  r += uSH[1] * 1.023328 * y;
  r += uSH[2] * 1.023328 * z;
  r += uSH[3] * 1.023328 * x;
  r += uSH[4] * 0.858086 * x * y;
  r += uSH[5] * 0.858086 * y * z;
  r += uSH[6] * (0.743125 * z * z - 0.247708);
  r += uSH[7] * 0.858086 * x * z;
  r += uSH[8] * 0.429043 * (x * x - y * y);
  return max(r, vec3(0.0));
}

vec3 hemiAmbient(vec3 n) {
  return shIrradiance(n) * (uAmbIntensity / 3.14159265);
}

// Painterly light: wrapped diffuse pushed into broad soft bands, cool tinted shadows, hemispheric fill.
vec3 paintLight(vec3 albedo, vec3 n, float shadow, float ao) {
  float ndl = dot(n, uSunDir);
  float w = clamp((ndl + uWrap) / (1.0 + uWrap), 0.0, 1.0);
  // Soft, wide terminator: stylized but never harsh.
  float band = smoothstep(0.0, 0.85, w);
  float lit = band * shadow;
  vec3 amb = hemiAmbient(n) * ao;
  // Shadows shift hue toward the palette's shadow colour without crushing value: painterly,
  // readable shade (a 4–5:1 lit/shade ratio rather than a physical 10:1).
  vec3 tintHue = uShadowTint / max(dot(uShadowTint, vec3(0.2126, 0.7152, 0.0722)), 1e-3);
  vec3 shadeTint = mix(mix(vec3(1.0), tintHue, 0.14), vec3(1.0), lit);
  return albedo * (amb * shadeTint + uSunColor * lit);
}

float fresnelRim(vec3 n, vec3 v, float p) {
  return pow(1.0 - clamp(dot(n, v), 0.0, 1.0), p);
}
`;

/** three.js chunk includes needed to read the directional shadow map from a custom ShaderMaterial (lights: true). */
export const SHADOW_FRAG_PARS = /* glsl */ `
#include <common>
#include <packing>
#include <lights_pars_begin>
#include <shadowmap_pars_fragment>
#include <shadowmask_pars_fragment>
`;

export const SHADOW_VERT_PARS = /* glsl */ `
#include <common>
#include <shadowmap_pars_vertex>
`;

/**
 * Call after computing (vec4 wPos) world position and (vec3 wNormal) world normal.
 * Provides what three's shadowmap_vertex chunk expects.
 */
export const SHADOW_VERT_MAIN = /* glsl */ `
  {
    vec4 worldPosition = wPos;
    vec3 transformedNormal = normalize((viewMatrix * vec4(wNormal, 0.0)).xyz);
    vec4 mvPosition = viewMatrix * wPos;
    #include <shadowmap_vertex>
  }
`;

/** Shared triplanar painterly-brush sampler. tBrush channels: R dabs, G vertical strokes, B grain, A blotches. */
export const BRUSH = /* glsl */ `
uniform sampler2D tBrush;
vec4 triBrush(vec3 p, vec3 n, float scale) {
  vec3 w = pow(abs(n), vec3(4.0));
  w /= (w.x + w.y + w.z);
  vec4 x = texture2D(tBrush, p.zy * scale);
  vec4 y = texture2D(tBrush, p.xz * scale);
  vec4 z = texture2D(tBrush, p.xy * scale);
  return x * w.x + y * w.y + z * w.z;
}
`;
